import { isTauri } from '@tauri-apps/api/core'

import { type Redacted } from '@blade/std-lib'

import { BrowserLocalPlatformUtil } from './browser-local-platform-util'
import { BrowserPlatformUtil } from './browser-platform-util'
import { TauriPlatformUtil } from './tauri-platform-util'

import type { UnknownError } from '../Error'
import type { DeviceId } from './device-id'
import type { PlatformError } from './platform-error'
import type { PlatformUtilType } from './platform-util-type'
import type { Result } from '@blade/std-lib'
import type { Platform } from '@tauri-apps/plugin-os'

export type ImpactHapticFeedbackStyle =
  | 'light'
  | 'medium'
  | 'heavy'
  | 'rigid'
  | 'soft'

export interface PlatformUpdate {
  version: string
  currentVersion: string
  notes?: string | undefined
  install: () => Promise<Result.Result<void, PlatformError>>
  download: (options: {
    onStarted: (payload: { contentLength?: number | undefined }) => void
    onProgress: (payload: { chunkLength: number }) => void
    onFinished: () => void
  }) => Promise<Result.Result<void, PlatformError>>
}

export interface PlatformUtil {
  getDeviceId: () => Promise<DeviceId>
  setScaleFactor: (
    scaleFactor: number,
  ) => Promise<Result.Result<number, [PlatformError, number]>>
  platform: PlatformUtilType
  is: (platform: PlatformUtilType) => boolean
  getVersion: () => Promise<string>
  openLink: (
    href: URL | string,
    target?: '_blank' | '_self',
  ) => Promise<Result.Result<void, PlatformError>>
  impactFeedback: (style: ImpactHapticFeedbackStyle) => Promise<void>
  copyToClipboard: (text: string | Redacted.Redacted) => Promise<boolean>
  copyImageToClipboard: (
    blob: Blob,
    mimeType: string,
  ) => Promise<Result.Result<void, PlatformError>>
  readFromClipboard: () => Promise<
    Result.Result<string | undefined, UnknownError>
  >
  locale: () => Promise<string | undefined>
  hostPlatform: () => undefined | Platform
  share: (data?: ShareData) => Promise<Result.Result<void, PlatformError>>
  deeplink: {
    getCurrent: () => Promise<Array<string> | undefined>
    onOpenUrl: (handler: (urls: Array<string>) => void) => Promise<() => void>
  }
  window: {
    minimize: () => Promise<void>
    toggleMaximize: () => Promise<void>
    close: () => Promise<void>
  }
  downloadFileFromURL: (
    url: URL | string,
    fileName: string,
  ) => Promise<Result.Result<void, PlatformError>>
  downloadFile: (
    url: Blob,
    fileName: string,
  ) => Promise<Result.Result<void, PlatformError>>
  relaunch: () => Promise<Result.Result<void, PlatformError>>
  check: (options?: {
    timeout?: number
  }) => Promise<Result.Result<PlatformUpdate | undefined, PlatformError>>
}

/** Browser-local mode: running in a browser but connected to a local Tauri process via HTTP */

export const isBrowserLocalMode = (): boolean =>
  !!(globalThis as Record<string, unknown>).__DELUGE_BROWSER_LOCAL__

const fallbackPlatformUtil = isBrowserLocalMode()
  ? BrowserLocalPlatformUtil
  : BrowserPlatformUtil

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PlatformUtil =
  isTauri() || import.meta.env.VITE_DEMO_MODE_SIMULATE_TAURI === 'true'
    ? TauriPlatformUtil
    : fallbackPlatformUtil
